import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PublicOrderTemplatesAPI, PublicOrdersAPI, CustomersPublicAPI } from '../services/api';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FiGlobe, FiMapPin, FiPhone, FiLogOut, FiArrowRight, FiChevronLeft, FiCheckCircle, FiMinus, FiPlus } from 'react-icons/fi';
import './PlaceOrder.css';
import NotFound from './NotFound';

// Fix Leaflet default icon paths for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export default function PlaceOrder() {
  const { companySlug, templateSlug } = useParams();
  const [searchParams] = useSearchParams();
  const templateIdFromQuery = searchParams.get('tid');

  const [template, setTemplate] = useState(null);
  const [items, setItems] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [toast, setToast] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authName, setAuthName] = useState('');
  const isSignedIn = useMemo(() => !!authEmail, [authEmail]);
  const [posStateCode, setPosStateCode] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const [notes, setNotes] = useState('');
  const [imageIndexByKey, setImageIndexByKey] = useState({});
  const swipeStartXRef = useRef({});
  const swipeDidRef = useRef({});
  const [lightbox, setLightbox] = useState({ open: false, title: '', images: [], index: 0 });
  const lightboxSwipeStartXRef = useRef(null);

  // Selected items quantities keyed by group index and line index
  const [selections, setSelections] = useState({}); // { `${gi}-${idx}`: { qty, note } }

  // Personal and shipping details
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '', gstin: '' });
  const [shipping, setShipping] = useState({ houseNumber: '', building: '', location: null, resolvedAddress: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '' });

  const controlStyle = { fontSize: 16, padding: '6px 8px', height: 38 };
  const money = useMemo(() => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }), []);

  const getPrimaryImage = (it) => {
    if (Array.isArray(it?.images) && it.images.length) return it.images[0];
    return it?.imageUrl || it?.image || it?.photo || it?.thumbnail || '';
  };

  const getImages = (it) => {
    const arr = Array.isArray(it?.images) ? it.images.filter(Boolean) : [];
    if (arr.length) return arr;
    const single = getPrimaryImage(it);
    return single ? [single] : [];
  };

  const getInitials = (name) => {
    const s = String(name || '').trim();
    if (!s) return 'P';
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return (parts[0].slice(0, 1) + parts[1].slice(0, 1)).toUpperCase();
  };

  const INDIAN_STATES = useMemo(() => [
    { code: '01', name: 'Jammu & Kashmir' },
    { code: '02', name: 'Himachal Pradesh' },
    { code: '03', name: 'Punjab' },
    { code: '04', name: 'Chandigarh' },
    { code: '05', name: 'Uttarakhand' },
    { code: '06', name: 'Haryana' },
    { code: '07', name: 'Delhi' },
    { code: '08', name: 'Rajasthan' },
    { code: '09', name: 'Uttar Pradesh' },
    { code: '10', name: 'Bihar' },
    { code: '11', name: 'Sikkim' },
    { code: '12', name: 'Arunachal Pradesh' },
    { code: '13', name: 'Nagaland' },
    { code: '14', name: 'Manipur' },
    { code: '15', name: 'Mizoram' },
    { code: '16', name: 'Tripura' },
    { code: '17', name: 'Meghalaya' },
    { code: '18', name: 'Assam' },
    { code: '19', name: 'West Bengal' },
    { code: '20', name: 'Jharkhand' },
    { code: '21', name: 'Odisha' },
    { code: '22', name: 'Chhattisgarh' },
    { code: '23', name: 'Madhya Pradesh' },
    { code: '24', name: 'Gujarat' },
    { code: '26', name: 'Daman and Diu, Dadra & Nagar Haveli' },
    { code: '27', name: 'Maharashtra' },
    { code: '28', name: 'Andhra Pradesh (before bifurcation)' },
    { code: '36', name: 'Telangana' },
    { code: '37', name: 'Andhra Pradesh (New)' },
    { code: '29', name: 'Karnataka' },
    { code: '30', name: 'Goa' },
    { code: '31', name: 'Lakshadweep' },
    { code: '32', name: 'Kerala' },
    { code: '33', name: 'Tamil Nadu' },
    { code: '34', name: 'Puducherry' },
    { code: '35', name: 'Andaman & Nicobar Islands' },
    { code: '38', name: 'Ladakh' }
  ], []);

  useEffect(() => {
    (async () => {
      try {
        if (templateIdFromQuery) {
          const res = await PublicOrderTemplatesAPI.get(templateIdFromQuery);
          setTemplate(res?.template || null);
          if (Array.isArray(res?.items)) setItems(res.items);
          if (res?.company) setCompany(res.company);
        } else if (companySlug && templateSlug) {
          const res = await PublicOrderTemplatesAPI.getBySlug(companySlug, templateSlug);
          setTemplate(res?.template || null);
          if (Array.isArray(res?.items)) setItems(res.items);
          if (res?.company) setCompany(res.company);
        }
      } catch (e) { console.error('Failed to load template', e); }
      finally { setLoading(false); }
    })();
  }, [templateIdFromQuery, companySlug, templateSlug]);

  // Items are provided by public endpoint for referenced items, so no auth call here

  useEffect(() => {
    if (!toast) return; const t = setTimeout(() => setToast(''), 2000); return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (step === 3) {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) { window.scrollTo(0, 0); }
    }
  }, [step]);

  const keyFor = (gi, idx) => `${gi}-${idx}`;
  const setQty = (gi, idx, qty) => setSelections(s => ({ ...s, [keyFor(gi, idx)]: { ...(s[keyFor(gi, idx)] || {}), qty } }));
  const setNote = (gi, idx, note) => setSelections(s => ({ ...s, [keyFor(gi, idx)]: { ...(s[keyFor(gi, idx)] || {}), note } }));

  const clampQty = (raw, minPick, maxPick) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    const n = Number(raw);
    if (Number.isNaN(n)) return '';
    const clamped = Math.min(Math.max(n, 0), Number.isFinite(maxPick) ? maxPick : n);
    // If user typed something > 0, enforce minPick (but keep 0 as a valid "not selected")
    const enforced = clamped > 0 ? Math.max(clamped, Number(minPick || 0)) : 0;
    return enforced;
  };

  const bumpQty = (gi, idx, line, delta) => {
    const k = keyFor(gi, idx);
    const current = Number((selections[k]?.qty ?? 0) || 0);
    const minPick = Number(line?.minQty ?? 0);
    const maxRaw = Number(line?.maxQty ?? 0);
    const hasLimit = maxRaw > 0;
    const maxPick = hasLimit ? maxRaw : Infinity;
    let next = current + delta;
    // Allow decrement to 0 (clear selection). If increment from 0, go to minPick if provided, else 1.
    if (current <= 0 && delta > 0) next = Math.max(1, minPick || 0);
    next = clampQty(next, minPick, maxPick);
    setQty(gi, idx, next);
  };

  const onIconKeyDown = (e, handler) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

  const reverseGeocode = async (coords) => {
    try {
      if (!coords?.lat || !coords?.lng) return;
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=18&addressdetails=1`,
        { headers: { 'User-Agent': 'BloomNav' } }
      );
      const data = await resp.json();
      const display = data?.display_name || '';
      const st = data?.address?.state || '';
      const stateMatch = INDIAN_STATES.find(s => (st || '').toLowerCase().includes(s.name.toLowerCase()));
      setPosStateCode(stateMatch?.code || '');
      setShipping((s) => ({ ...s, resolvedAddress: display }));
    } catch (_) {
      // ignore
    }
  };

  const openLightbox = (title, images, index = 0) => {
    const imgs = Array.isArray(images) ? images.filter(Boolean) : [];
    if (!imgs.length) return;
    const idx = Math.max(0, Math.min(Number(index || 0), imgs.length - 1));
    setLightbox({ open: true, title: title || '', images: imgs, index: idx });
  };

  const closeLightbox = () => setLightbox({ open: false, title: '', images: [], index: 0 });
  const lightboxNext = () => setLightbox((s) => ({ ...s, index: s.images.length ? (s.index + 1) % s.images.length : 0 }));
  const lightboxPrev = () => setLightbox((s) => ({ ...s, index: s.images.length ? (s.index - 1 + s.images.length) % s.images.length : 0 }));

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') lightboxNext();
      if (e.key === 'ArrowLeft') lightboxPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox.open]);

  const groups = useMemo(() => Array.isArray(template?.itemGroups) ? template.itemGroups : [], [template]);
  const sectionsGroups = useMemo(() => Array.isArray(template?.sectionsGroups) ? template.sectionsGroups : [], [template]);
  const [activeCategory, setActiveCategory] = useState('ALL');

  const categories = useMemo(() => {
    const base = [{ key: 'ALL', label: 'All' }];
    const seen = new Set();
    groups.forEach((g, gi) => {
      const label = (g?.title || '').trim() || `Category ${gi + 1}`;
      const key = `G-${gi}`;
      if (seen.has(key)) return;
      seen.add(key);
      base.push({ key, label, gi });
    });
    return base;
  }, [groups]);

  useEffect(() => {
    // If categories change, ensure activeCategory remains valid
    if (activeCategory === 'ALL') return;
    const ok = categories.some(c => c.key === activeCategory);
    if (!ok) setActiveCategory('ALL');
  }, [categories, activeCategory]);

  const addressText = useMemo(() => {
    const a = company?.address;
    if (!a) return '';
    if (typeof a === 'string') return a;
    if (typeof a === 'object') {
      const parts = [a.addressLine1, a.addressLine2, a.city, a.state, a.pincode, a.zip, a.country].filter(Boolean);
      if (parts.length) return parts.join(', ');
      const generic = Object.values(a || {}).filter(v => typeof v === 'string').join(', ');
      return generic;
    }
    return '';
  }, [company]);

  const pinIcon = useMemo(() => {
    try {
      return L.divIcon({
        className: '',
        html: '<div style="width:16px;height:16px;border-radius:9999px;background:#2563eb;border:2px solid #ffffff;box-shadow:0 2px 8px rgba(0,0,0,0.35)"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });
    } catch (e) {
      return undefined;
    }
  }, []);

  const selectedCount = useMemo(() => {
    return Object.values(selections).reduce((acc, s) => acc + (Number(s?.qty || 0) > 0 ? 1 : 0), 0);
  }, [selections]);




  const lineSubtotal = (line, sel) => {
    const qty = Number(sel?.qty || 0);
    const price = Number(line.price || 0);
    const discAmt = Number(line.discountAmount || 0);
    const discPct = Number(line.discountPercent || 0);
    const base = qty * price;
    const afterAmt = Math.max(base - discAmt, 0);
    const afterPct = Math.max(afterAmt - (afterAmt * discPct) / 100, 0);
    return afterPct;
  };

  const summaryItems = useMemo(() => {
    const list = [];
    groups.forEach((g, gi) => {
      (g.lines || []).forEach((l, idx) => {
        const sel = selections[keyFor(gi, idx)] || {};
        const qty = Number(sel.qty || 0);
        if (qty > 0) {
          const it = items.find(x => String(x.id) === String(l.itemId));
          const name = it?.name || l.description || 'Item';
          const section = g?.title || '';
          list.push({ name, section, qty, subtotal: lineSubtotal(l, sel) });
        }
      });
    });
    return list;
  }, [groups, items, selections]);

  const total = useMemo(() => {
    return groups.reduce((acc, g, gi) => acc + (g.lines || []).reduce((acc2, l, idx) => acc2 + lineSubtotal(l, selections[keyFor(gi, idx)]), 0), 0);
  }, [groups, selections]);

  const gstSummary = useMemo(() => {
    // Derive simple intra/inter state from company and posStateCode
    const companyState = company?.stateCode;
    const pos = posStateCode || companyState;
    const isInter = companyState && pos && companyState !== pos;
    const noGst = !company?.gstin;
    let cgst = 0, sgst = 0, igst = 0, taxable = 0, totalWithTax = 0;
    groups.forEach((g, gi) => {
      (g.lines || []).forEach((l, idx) => {
        const sel = selections[keyFor(gi, idx)] || {};
        const qty = Number(sel.qty || 0);
        if (qty <= 0) return;
        const price = Number(l.price || 0);
        const discAmt = Number(l.discountAmount || 0);
        const discPct = Number(l.discountPercent || 0);
        const gross = qty * price;
        const netBeforeTax = Math.max(0, gross - discAmt - (discPct / 100) * gross);
        const cgRate = Number(l.cgstRate || 0);
        const sgRate = Number(l.sgstRate || 0);
        const igRate = Number(l.igstRate || 0);
        const cg = (noGst || isInter) ? 0 : +(netBeforeTax * cgRate / 100).toFixed(2);
        const sg = (noGst || isInter) ? 0 : +(netBeforeTax * sgRate / 100).toFixed(2);
        const ig = (noGst || !isInter) ? 0 : +(netBeforeTax * (igRate || (cgRate + sgRate)) / 100).toFixed(2);
        taxable += netBeforeTax; cgst += cg; sgst += sg; igst += ig; totalWithTax += netBeforeTax + cg + sg + ig;
      });
    });
    if (noGst) {
      return { cgst: 0, sgst: 0, igst: 0, taxable, totalWithTax: taxable };
    }
    return { cgst, sgst, igst, taxable, totalWithTax };
  }, [groups, selections, company?.stateCode, posStateCode]);

  const deriveStateCodeFromGSTIN = (gstin) => {
    const valid = new Set(['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36', '37']);
    if (!gstin || typeof gstin !== 'string') return '';
    const code = String(gstin).slice(0, 2);
    return valid.has(code) ? code : '';
  };

  const deriveCustomerStateCode = () => {
    // 1) GSTIN prefix, 2) selected/derived shipping state match, 3) posStateCode from geocode
    const fromGstin = deriveStateCodeFromGSTIN(customer.gstin);
    if (fromGstin) return fromGstin;
    const byStateName = INDIAN_STATES.find(s => (shipping.state || '').toLowerCase().includes(s.name.toLowerCase()))?.code || '';
    if (byStateName) return byStateName;
    return posStateCode || '';
  };

  const canContinue = useMemo(() => {
    if (step === 1) {
      // at least one qty > 0
      return Object.values(selections).some(s => Number(s.qty || 0) > 0);
    }
    if (step === 2) {
      const hasItems = Object.values(selections).some(s => Number(s.qty || 0) > 0);
      const hasBasics = !!customer.name && (!!customer.phone || !!customer.email);
      const hasAddress = !!shipping.houseNumber && !!shipping.location;
      return hasItems && hasBasics && hasAddress;
    }
    return true;
  }, [step, selections, customer, shipping]);

  const hasSelection = useMemo(() => Object.values(selections).some(s => Number(s.qty || 0) > 0), [selections]);

  const buildOrderItemsPayload = () => {
    const itemsPayload = [];
    groups.forEach((g, gi) => {
      (g.lines || []).forEach((l, idx) => {
        const sel = selections[keyFor(gi, idx)] || {};
        const qty = Number(sel.qty || 0);
        if (qty > 0) {
          itemsPayload.push({
            itemId: l.itemId ?? null,
            description: (g?.title || l.description),
            quantity: qty,
            unit: l.unit,
            price: Number(l.price || 0),
            discountAmount: Number(l.discountAmount || 0),
            discountPercent: Number(l.discountPercent || 0)
          });
        }
      });
    });
    return itemsPayload;
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential || '') || {};
      const email = decoded.email || '';
      const name = decoded.name || '';
      setAuthEmail(email);
      setAuthName(name);
      if (step < 2) setStep(2);
      // Prefill customer details if they are a customer of this company
      const companyId = template?.companyId || company?.id;
      if (email && companyId) {
        try {
          const res = await CustomersPublicAPI.getByEmail(email, companyId);
          if (res?.is_success && res?.customer) {
            const c = res.customer;
            setCustomer({ name: c.name || name || '', email: c.email || email, phone: c.phone || '', gstin: c.gstin || '' });
            const addr = c.address || {};
            // Merge (don't overwrite) so required fields like houseNumber/location aren't lost
            setShipping((prev) => ({
              ...prev,
              addressLine1: addr.addressLine1 || prev.addressLine1 || '',
              addressLine2: addr.addressLine2 || prev.addressLine2 || '',
              city: addr.city || prev.city || '',
              state: addr.state || prev.state || '',
              pincode: addr.pincode || addr.zip || prev.pincode || ''
            }));
            setToast('Details prefilled from your customer profile');
          } else {
            // Not found - at least fill name/email
            setCustomer((prev) => ({ ...prev, name: prev.name || name, email: prev.email || email }));
          }
        } catch (e) {
          // 404 or other - fallback to name/email prefill
          setCustomer((prev) => ({ ...prev, name: prev.name || name, email: prev.email || email }));
        }
      }
    } catch (e) {
      console.error('Google decode failure', e);
    }
  };

  const handleLogout = () => {
    setAuthEmail('');
    setAuthName('');
  };

  const handlePlaceOrder = async () => {
    try {
      const companyId = template?.companyId || company?.id;
      const email = (customer.email || authEmail || '').trim();
      const name = (customer.name || authName || '').trim();
      // Validate required fields; highlight missing
      const hasItems = Object.values(selections).some(s => Number(s.qty || 0) > 0);
      const missingName = !name;
      const missingPhone = !customer.phone;
      const missingEmail = !email;
      const missingIdentifier = missingPhone && missingEmail;
      const missingHouse = !shipping.houseNumber;
      const missingLocation = !shipping.location;
      const hasMissing = !hasItems || missingName || missingIdentifier || missingHouse || missingLocation;
      if (hasMissing) {
        setShowErrors(true);
        setToast('Please fill all required fields');
        return;
      }

      // Try to ensure customer exists or will be created by backend
      const address = {
        houseNumber: shipping.houseNumber || '',
        building: shipping.building || '',
        addressLine1: shipping.addressLine1 || '',
        addressLine2: shipping.addressLine2 || '',
        city: shipping.city || '',
        state: shipping.state || '',
        pincode: shipping.pincode || '',
        resolvedAddress: shipping.resolvedAddress || '',
        location: shipping.location || null,
        stateCode: deriveCustomerStateCode() || ''
      };
      const itemsPayload = buildOrderItemsPayload();
      if (itemsPayload.length === 0) { setToast('Select at least one item'); return; }

      const payload = templateIdFromQuery ? {
        templateId: templateIdFromQuery,
        customer: { name, email, phone: customer.phone || '', gstin: customer.gstin || '', stateCode: deriveCustomerStateCode() || '', address },
        items: itemsPayload,
        notes: notes || null,
        createdFrom: 'CUSTOMER_ORDER'
      } : {
        companySlug,
        templateSlug,
        customer: { name, email, phone: customer.phone || '', gstin: customer.gstin || '', stateCode: deriveCustomerStateCode() || '', address },
        items: itemsPayload,
        notes: notes || null,
        createdFrom: 'CUSTOMER_ORDER'
      };
      const res = await PublicOrdersAPI.placeOrder(payload);
      if (res?.success) {
        setToast('Order placed successfully');
        setStep(3);
        setSelections({});
        setNotes('');
        setSuccessInfo({ number: res.orderNumber || res.number || null, orderId: res.orderId || null, quoteId: res.quoteId || null, quoteNumber: res.quoteNumber || null });
      } else {
        setToast(res?.error || 'Failed to place order');
      }
    } catch (e) {
      console.error('Place order failed', e);
      setToast(e?.response?.data?.error || 'Failed to place order');
    }
  };

  if (loading) {
    return (
      <div className="page-content po-page">
        <div className="po-container">
          <div className="modern-loading-state">
            <div className="loading-spinner-container">
              <div className="loading-spinner modern" />
              <div className="loading-text">
                <h3>Loading</h3>
                <p>Please wait while we prepare your order page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return <NotFound />;
  }

  return (
    <div className="page-content po-page">
      <div className="po-container">
        {!!toast && (
          <div className="po-toast" role="status" aria-live="polite">
            <div className="po-toast__inner">{toast}</div>
          </div>
        )}

        <div className="po-hero">
          <div className="po-hero__content">
            {!!company?.logo && <img className="po-hero__logo" src={company.logo} alt={company?.name || 'Logo'} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 className="po-hero__title">{company?.name || 'Shop'}</h1>
              {!!addressText && (
                <div className="po-hero__meta" title={addressText}>
                  <FiMapPin style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                  {addressText}
                </div>
              )}
              <div className="po-hero__metaRow">
                {!!company?.phone && (
                  <a className="po-pill" href={`tel:${company.phone}`} title="Call">
                    <FiPhone />
                    <span>{company.phone}</span>
                  </a>
                )}
                {!!company?.website && (
                  <a className="po-pill" href={company.website} target="_blank" rel="noreferrer" title="Website">
                    <FiGlobe />
                    <span style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.website}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {step === 3 ? (
          <div className="po-card" style={{ maxWidth: 760, margin: '16px auto 0' }}>
            <div className="po-card__body" style={{ textAlign: 'center', padding: 18 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 54, height: 54, borderRadius: 9999, background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)', marginBottom: 12 }}>
                <FiCheckCircle size={26} color="#10b981" />
              </div>
              <h2 style={{ margin: '4px 0 6px', fontSize: 22, fontWeight: 700 }}>Order placed</h2>
              <div className="po-muted" style={{ marginBottom: 14 }}>We’ve received your order and will contact you shortly.</div>
              {!!successInfo?.number && (
                <div style={{ marginBottom: 14 }}>
                  <span className="po-muted">Order number:</span>{' '}
                  <span style={{ fontWeight: 700 }}>{successInfo.number}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={() => { setShowErrors(false); setStep(1); }}>
                  Place another order
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="po-layout">
            <div>
              {step === 1 && (
                <div className="po-card">
                  <div className="po-card__header">
                    <div className="po-stepper2" aria-label="Checkout steps">
                      <div
                        className={`po-stepper2__step ${step === 1 ? 'active' : 'done'} ${step > 1 ? 'clickable' : ''}`}
                        role={step > 1 ? 'button' : undefined}
                        tabIndex={step > 1 ? 0 : undefined}
                        onClick={() => { if (step > 1) { setShowErrors(false); setStep(1); } }}
                        onKeyDown={(e) => { if (step > 1 && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setShowErrors(false); setStep(1); } }}
                      >
                        <span className="po-stepper2__circle">1</span>
                        <span className="po-stepper2__label">Cart</span>
                      </div>
                      <div className="po-stepper2__line" aria-hidden="true" />
                      <div className={`po-stepper2__step ${step === 2 ? 'active' : ''}`}>
                        <span className="po-stepper2__circle">2</span>
                        <span className="po-stepper2__label">Delivery</span>
                      </div>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <div className="po-card__sub">{selectedCount} selected</div>
                    </div>
                  </div>
                  <div className="po-card__body">
                    <div className="po-products">
                      <div className="po-categoryBar" aria-label="Filter by category">
                        {categories.map((c) => (
                          <button
                            key={c.key}
                            type="button"
                            className={`po-categoryChip ${activeCategory === c.key ? 'active' : ''}`}
                            onClick={() => setActiveCategory(c.key)}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>

                      {groups
                        .filter((_, gi) => activeCategory === 'ALL' || activeCategory === `G-${gi}`)
                        .map((group, gi) => (
                        <div key={gi} className="po-productGroup">
                          <div className="po-productGroup__title">{group.title || 'Items'}</div>
                          <div className="po-productGrid">
                      {(group.lines || []).map((l, idx) => {
                        const sel = selections[keyFor(gi, idx)] || {};
                        const it = items.find(x => String(x.id) === String(l.itemId));
                              const unit = (l.unit || it?.unit || 'unit').toLowerCase();
                              const minPick = Number(l.minQty ?? 0);
                              const maxRaw = Number(l.maxQty ?? 0);
                              const hasLimit = maxRaw > 0; // max=0 means "no limit"
                              const maxPick = hasLimit ? maxRaw : Infinity;
                              const qtyNum = Number(sel.qty || 0);
                        return (
                                <div key={idx} className="po-productCard">
                                  {(() => {
                                    const title = it?.name || l.description || 'Product';
                                    const imgs = getImages(it);
                                    const imgKey = keyFor(gi, idx);
                                    const activeIdx = Math.max(0, Math.min(Number(imageIndexByKey[imgKey] || 0), Math.max(imgs.length - 1, 0)));
                                    const activeImg = imgs[activeIdx] || '';
                                    const setActiveIdx = (next) => {
                                      if (!imgs.length) return;
                                      const n = ((next % imgs.length) + imgs.length) % imgs.length;
                                      setImageIndexByKey((s) => ({ ...s, [imgKey]: n }));
                                    };
                                    return (
                                      <>
                                  <div
                                    className="po-productCard__carousel"
                                    role={activeImg ? 'button' : undefined}
                                    tabIndex={activeImg ? 0 : undefined}
                                    aria-label={activeImg ? `View image for ${title}` : undefined}
                                    onPointerDown={(e) => { swipeStartXRef.current[imgKey] = e.clientX; }}
                                    onPointerUp={(e) => {
                                      const start = swipeStartXRef.current[imgKey];
                                      if (typeof start !== 'number') return;
                                      const dx = e.clientX - start;
                                      swipeStartXRef.current[imgKey] = undefined;
                                      if (Math.abs(dx) < 40) return;
                                      swipeDidRef.current[imgKey] = true;
                                      window.setTimeout(() => { swipeDidRef.current[imgKey] = false; }, 250);
                                      if (dx < 0) setActiveIdx(activeIdx + 1);
                                      else setActiveIdx(activeIdx - 1);
                                    }}
                                    onPointerCancel={() => { swipeStartXRef.current[imgKey] = undefined; }}
                                    onClick={() => { if (swipeDidRef.current[imgKey]) return; if (imgs.length) openLightbox(title, imgs, activeIdx); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (imgs.length) openLightbox(title, imgs, activeIdx); } }}
                                  >
                                    {activeImg ? (
                                      <img className="po-productCard__imgLarge" src={activeImg} alt="" loading="lazy" draggable={false} />
                                    ) : (
                                      <div className="po-productCard__imgFallbackLarge">{getInitials(title)}</div>
                                    )}
                                    {imgs.length > 1 && (
                                      <>
                                        <button
                                          type="button"
                                          className="po-productCard__arrow left"
                                          aria-label="Previous image"
                                          onClick={(ev) => { ev.stopPropagation(); setActiveIdx(activeIdx - 1); }}
                                        >
                                          ‹
                                        </button>
                                        <button
                                          type="button"
                                          className="po-productCard__arrow right"
                                          aria-label="Next image"
                                          onClick={(ev) => { ev.stopPropagation(); setActiveIdx(activeIdx + 1); }}
                                        >
                                          ›
                                        </button>
                                      </>
                                    )}
                                    {imgs.length > 1 && (
                                      <div className="po-productCard__dots" aria-hidden="true">
                                        {imgs.map((_, di) => (
                                          <span
                                            key={di}
                                            className={`po-productCard__dot ${di === activeIdx ? 'active' : ''}`}
                                            onClick={(ev) => { ev.stopPropagation(); setActiveIdx(di); }}
                                          />
                                        ))}
                                </div>
                                    )}
                                </div>

                                  <div className="po-productCard__top">
                                    <div className="po-productCard__info">
                                      <div className="po-productCard__name">{title}</div>
                                      <div className="po-productCard__priceInline">
                                        <span className="po-productCard__priceValue">{money.format(Number(l.price || 0))}</span>
                                        <span className="po-productCard__unit">/ {unit}</span>
                                  </div>
                                </div>
                                </div>

                                  {!!l.description && (
                                    <div className="po-productCard__descFull">
                                      <div className="po-productCard__desc">{l.description}</div>
                              </div>
                                  )}

                                  <div className="po-productCard__controls">
                                    <div className="po-qtyLabelRow">
                                      <span className="po-qtyLabel">Quantity</span>
                                      {hasLimit && (minPick > 0 || maxRaw > 0) ? (
                                        <span className="po-minmax">
                                          {minPick > 0 ? `Min ${minPick}` : null}
                                          {minPick > 0 && maxRaw > 0 ? ' • ' : null}
                                          {maxRaw > 0 ? `Max ${maxRaw}` : null}
                                        </span>
                                      ) : null}
                                  </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                      <div className="po-qty">
                                        <span
                                          className={`po-qty__icon ${qtyNum <= 0 ? 'disabled' : ''}`}
                                          role="button"
                                          tabIndex={qtyNum <= 0 ? -1 : 0}
                                          aria-label="Decrease quantity"
                                          aria-disabled={qtyNum <= 0}
                                          onClick={() => { if (qtyNum <= 0) return; bumpQty(gi, idx, l, -1); }}
                                          onKeyDown={(e) => onIconKeyDown(e, () => bumpQty(gi, idx, l, -1))}
                                        >
                                          <FiMinus size={18} />
                                        </span>
                                        <input
                                          className="po-qty__input"
                                          type="number"
                                          min={0}
                                          {...(hasLimit ? { max: maxRaw } : {})}
                                          step="any"
                                          inputMode="decimal"
                                          value={sel.qty ?? ''}
                                          onChange={(e) => setQty(gi, idx, clampQty(e.target.value === '' ? '' : e.target.value, minPick, maxPick))}
                                          aria-label="Quantity"
                                        />
                                        <span
                                          className={`po-qty__icon ${(hasLimit ? qtyNum >= maxRaw : false) ? 'disabled' : ''}`}
                                          role="button"
                                          tabIndex={(hasLimit ? qtyNum >= maxRaw : false) ? -1 : 0}
                                          aria-label="Increase quantity"
                                          aria-disabled={(hasLimit ? qtyNum >= maxRaw : false)}
                                          onClick={() => { if (hasLimit && qtyNum >= maxRaw) return; bumpQty(gi, idx, l, +1); }}
                                          onKeyDown={(e) => onIconKeyDown(e, () => bumpQty(gi, idx, l, +1))}
                                        >
                                          <FiPlus size={18} />
                                        </span>
                                    </div>
                                      <div className="po-qty__unit">/ {unit}</div>
                                  </div>
                                </div>

                                      </>
                                    );
                                  })()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                      ))}
                      {showErrors && !hasSelection && (
                        <div className="po-muted" style={{ marginTop: 6, color: '#991b1b' }}>
                          Please select at least one product to continue.
                        </div>
                      )}
                    </div>
                  </div>
              </div>
            )}

            {step === 2 && (
                <div className="po-products">
                  <div className="po-card">
                    <div className="po-card__header">
                      <div className="po-stepper2" aria-label="Checkout steps">
                        <div
                          className="po-stepper2__step done clickable"
                          role="button"
                          tabIndex={0}
                          onClick={() => { setShowErrors(false); setStep(1); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowErrors(false); setStep(1); } }}
                        >
                          <span className="po-stepper2__circle">1</span>
                          <span className="po-stepper2__label">Cart</span>
                        </div>
                        <div className="po-stepper2__line" aria-hidden="true" />
                        <div className="po-stepper2__step active">
                          <span className="po-stepper2__circle">2</span>
                          <span className="po-stepper2__label">Delivery</span>
                        </div>
                      </div>
                      <div className="po-card__sub">We’ll use this to confirm your order.</div>
                    </div>
                    <div className="po-card__body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                          <input className={`form-control ${showErrors && !customer.name ? 'po-error' : ''}`} style={controlStyle} value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                          <input className={`form-control ${showErrors && !customer.phone ? 'po-error' : ''}`} style={controlStyle} value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                          <input className={`form-control ${showErrors && !(customer.email || authEmail) ? 'po-error' : ''}`} style={controlStyle} value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>GSTIN (optional)</label>
                    <input className="form-control" placeholder="15-character GSTIN" style={controlStyle} value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value.toUpperCase() })} />
                        </div>
                      </div>
                  </div>
                </div>

                  <div className="po-card">
                    <div className="po-card__header">
                      <div>
                        <div className="po-card__title">Delivery address</div>
                        <div className="po-card__sub">Pin your location for accurate delivery.</div>
                      </div>
                    </div>
                    <div className="po-card__body">
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>House / Flat number</label>
                          <input className={`form-control ${showErrors && !shipping.houseNumber ? 'po-error' : ''}`} placeholder="e.g., A-103" style={controlStyle} value={shipping.houseNumber} onChange={(e) => setShipping({ ...shipping, houseNumber: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                          <label>Building / Landmark (optional)</label>
                          <input className="form-control" placeholder="e.g., Sunshine Residency" style={controlStyle} value={shipping.building} onChange={(e) => setShipping({ ...shipping, building: e.target.value })} />
                  </div>
                </div>

                      <div className="form-group" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={async () => {
                    if (!navigator.geolocation) { setToast('Geolocation not supported'); return; }
                    navigator.geolocation.getCurrentPosition(async (pos) => {
                      const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
                      setShipping((s) => ({ ...s, location: coords }));
                      await reverseGeocode(coords);
                      setToast('Location pinned');
                    }, (err) => setToast(err?.message || 'Unable to fetch location'), { enableHighAccuracy: true, timeout: 10000 });
                          }}
                        >
                          <FiMapPin style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                          Use current location
                        </button>
                        <div className="po-muted" style={{ fontSize: 12 }}>
                          {shipping.location ? `Location: ${shipping.location.lat.toFixed(5)}, ${shipping.location.lng.toFixed(5)}` : 'Select address on map'}
                        </div>
                </div>

                      {showErrors && !shipping.location && (
                        <div className="po-muted" style={{ color: '#991b1b', fontSize: 12, marginTop: 6 }}>
                          Please pin your delivery location to continue.
                        </div>
                      )}

                {!!shipping?.location && (
                        <div className={`po-mapShell ${showErrors && !shipping.location ? 'po-error' : ''}`} style={{ marginTop: 10 }}>
                        <MapContainer
                          center={[shipping.location.lat, shipping.location.lng]}
                          zoom={16}
                          scrollWheelZoom={false}
                            style={{ height: isMobile ? 240 : 280, width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; OpenStreetMap contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <Marker
                            position={[shipping.location.lat, shipping.location.lng]}
                            icon={pinIcon}
                            draggable
                            eventHandlers={{
                              dragend: async (e) => {
                                const latlng = e?.target?.getLatLng?.();
                                if (!latlng) return;
                                const coords = { lat: latlng.lat, lng: latlng.lng, accuracy: null };
                                setShipping((s) => ({ ...s, location: coords }));
                                await reverseGeocode(coords);
                              }
                            }}
                          >
                            <Popup>Selected location</Popup>
                          </Marker>
                        </MapContainer>
                  </div>
                )}

                      {!!shipping.resolvedAddress && (
                        <div className="po-muted" style={{ fontSize: 12, marginTop: 10 }}>
                          <strong style={{ color: '#111827' }}>Address:</strong> {shipping.resolvedAddress}
              </div>
            )}
          </div>
            </div>

                  <div className="po-card">
                    <div className="po-card__header">
                      <div>
                        <div className="po-card__title">Notes (optional)</div>
                        <div className="po-card__sub">Special instructions for delivery or packing.</div>
                </div>
                            </div>
                    <div className="po-card__body">
                      <textarea className="form-control" rows={3} placeholder="Any special instructions for this order" style={{ ...controlStyle, height: 'auto', lineHeight: '22px' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {sectionsGroups.map((sec, si) => (
                <details key={si} className="po-card" style={{ marginTop: 12 }}>
                  <summary className="po-card__header" style={{ cursor: 'pointer' }}>
                    <div className="po-card__title">{sec.title || 'Info'}</div>
                  </summary>
                  <div className="po-card__body">
                    {(sec.items || []).map((it, ii) => (
                      <div key={ii} style={{ padding: '5px 0', borderTop: ii === 0 ? 'none' : '1px solid #f3f4f6' }}>
                        <div style={{ fontWeight: 500 }}>{it.title}</div>
                        {!!it.description && <div className="po-muted" style={{ marginTop: 2 }}>{it.description}</div>}
                          </div>
                    ))}
                        </div>
                </details>
                      ))}
                    </div>

            <div className="po-summary">
              <div className="po-card">
                <div className="po-card__header">
                  <div>
                    <div className="po-card__title">Order summary</div>
                    <div className="po-card__sub">{selectedCount} items</div>
                  </div>
                  </div>
                <div className="po-card__body">
                  {summaryItems.length > 0 ? (
                    <div className="po-summary__list">
                      {summaryItems.map((s, i) => (
                        <div key={i} className="po-summaryItem">
                          <div style={{ minWidth: 0 }}>
                            <div className="po-summaryItem__name" title={s.name}>{s.name} <span className="po-muted" style={{ fontWeight: 700 }}>× {s.qty}</span></div>
                            {!!s.section && <div className="po-summaryItem__meta" title={s.section}>{s.section}</div>}
                  </div>
                          <div style={{ fontWeight: 500 }}>{money.format(s.subtotal)}</div>
                  </div>
                      ))}
                  </div>
                  ) : (
                    <div className="po-muted">No items selected yet.</div>
                  )}

                  <div className="po-divider" />
                  <div className="po-row"><span className="po-muted">Taxable</span><span>{money.format(gstSummary.taxable || 0)}</span></div>
                  <div className="po-row"><span className="po-muted">CGST</span><span>{money.format(gstSummary.cgst || 0)}</span></div>
                  <div className="po-row"><span className="po-muted">SGST</span><span>{money.format(gstSummary.sgst || 0)}</span></div>
                  <div className="po-row"><span className="po-muted">IGST</span><span>{money.format(gstSummary.igst || 0)}</span></div>
                  <div className="po-divider" />
                  <div className="po-total">
                    <span>Total</span>
                    <span className="po-total__amount">{money.format(gstSummary.totalWithTax || total)}</span>
                  </div>

                  {!isMobile && (
                    <div style={{ marginTop: 12 }}>
                  {step < 2 ? (
                    isSignedIn ? (
                        <button
                          className="btn btn-primary"
                          style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                          disabled={!canContinue}
                          onClick={() => {
                            if (!canContinue) { setShowErrors(true); setToast('Select at least one product'); return; }
                            setShowErrors(false);
                            setStep(2);
                          }}
                        >
                          Continue <FiArrowRight />
                        </button>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'center', opacity: hasSelection ? 1 : 0.5, pointerEvents: hasSelection ? 'auto' : 'none' }}>
                          <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setToast('Google login failed')} text="signin_with" shape="pill" size="large" />
                        </div>
                          {!hasSelection && <div className="po-muted" style={{ marginTop: 8, textAlign: 'center', fontSize: 12 }}>Select at least 1 item to proceed.</div>}
                      </>
                    )
                  ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <button
                          className="btn btn-secondary"
                          style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                          onClick={() => { setShowErrors(false); setStep(1); }}
                        >
                          <FiChevronLeft /> Back
                        </button>
                        <button
                          className="btn btn-primary"
                          style={{ width: '100%' }}
                          disabled={!canContinue}
                          onClick={() => {
                            if (!canContinue) { setShowErrors(true); setToast('Please fill all required fields'); return; }
                            handlePlaceOrder();
                          }}
                        >
                          Place order
                        </button>
                      </div>
                  )}
                    {isSignedIn && (
                      <div style={{ marginTop: 10, textAlign: 'center' }}>
                        <button className="po-logoutLink" onClick={handleLogout} type="button">
                          <FiLogOut />
                          Logout
                        </button>
                </div>
                    )}
            </div>
          )}
                </div>
              </div>
            </div>
        </div>
      )}
              </div>

      {lightbox.open && (
        <div className="po-lightbox" role="dialog" aria-modal="true" aria-label="Image viewer" onClick={closeLightbox}>
          <div className="po-lightbox__panel" onClick={(e) => e.stopPropagation()}>
            <div className="po-lightbox__header">
              <div className="po-lightbox__title" title={lightbox.title}>{lightbox.title || 'Image'}</div>
              <button className="po-lightbox__close" type="button" onClick={closeLightbox} aria-label="Close">✕</button>
          </div>

            <div
              className="po-lightbox__stage"
              onPointerDown={(e) => { lightboxSwipeStartXRef.current = e.clientX; }}
              onPointerUp={(e) => {
                const start = lightboxSwipeStartXRef.current;
                if (typeof start !== 'number') return;
                const dx = e.clientX - start;
                lightboxSwipeStartXRef.current = null;
                if (Math.abs(dx) < 50) return;
                if (dx < 0) lightboxNext();
                else lightboxPrev();
              }}
              onPointerCancel={() => { lightboxSwipeStartXRef.current = null; }}
            >
              <button className="po-lightbox__nav left" type="button" onClick={lightboxPrev} aria-label="Previous image">‹</button>
              <img className="po-lightbox__img" src={lightbox.images[lightbox.index]} alt="" draggable={false} />
              <button className="po-lightbox__nav right" type="button" onClick={lightboxNext} aria-label="Next image">›</button>
            </div>

            {lightbox.images.length > 1 && (
              <div className="po-lightbox__thumbs" aria-label="Image thumbnails">
                {lightbox.images.map((url, i) => (
                  <button
                    key={`${url}-${i}`}
                    type="button"
                    className={`po-lightbox__thumb ${i === lightbox.index ? 'active' : ''}`}
                    onClick={() => setLightbox((s) => ({ ...s, index: i }))}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={url} alt="" />
                  </button>
                ))}
            </div>
            )}
          </div>
        </div>
      )}

      {step !== 3 && (
        <div className="po-mobileBar">
          <div className="po-mobileBar__row">
            <div className="po-mobileBar__meta">
              <div className="po-mobileBar__label">Total</div>
              <div className="po-mobileBar__amount">{money.format(gstSummary.totalWithTax || total)}</div>
            </div>
            <div className={`po-mobileBar__actions ${step < 2 ? (isSignedIn ? 'single' : 'oauth') : 'double'}`}>
            {step < 2 ? (
              isSignedIn ? (
                  <button
                    className="btn btn-primary"
                    disabled={!canContinue}
                    onClick={() => {
                      if (!canContinue) { setShowErrors(true); setToast('Select at least one product'); return; }
                      setShowErrors(false);
                      setStep(2);
                    }}
                  >
                    Continue
                  </button>
              ) : (
                <div style={{ opacity: hasSelection ? 1 : 0.5, pointerEvents: hasSelection ? 'auto' : 'none' }}>
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setToast('Google login failed')} text="signin_with" shape="pill" size="large" />
                </div>
              )
            ) : (
              <>
                  <button className="btn btn-secondary" onClick={() => { setShowErrors(false); setStep(1); }}>Back</button>
                  <button
                    className="btn btn-primary"
                    disabled={!canContinue}
                    onClick={() => {
                      if (!canContinue) { setShowErrors(true); setToast('Please fill all required fields'); return; }
                      handlePlaceOrder();
                    }}
                  >
                    Place order
                  </button>
              </>
            )}
          </div>
          </div>
          {isSignedIn && (
            <div className="po-mobileBar__footer">
              <button className="po-logoutLink" onClick={handleLogout} type="button">
                <FiLogOut />
                Logout
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


